// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { AuditEventContextAction, ProjectCostTime } from '@/audit/api/types';
import { AuditPropertyValue } from '@/audit/components/audit-property-value';
import { ArrowForwardRounded } from '@/constants/hardfin-standard-icons';
import { Typography } from '@mui/material';
import { isEqual } from 'lodash-es';

/**
 * Props for the AuditProjectCostTime component
 */
interface Props {
  property: ProjectCostTime;
  action: AuditEventContextAction;
}

function renderForUpdate(props: Props) {
  const oldRate = props.property.oldValue?.rate ?? null;
  const newRate = props.property.newValue?.rate ?? null;
  const oldHours = props.property.oldValue?.hours ?? null;
  const newHours = props.property.newValue?.hours ?? null;
  const oldEmployee = props.property.oldValue?.employee ?? null;
  const newEmployee = props.property.newValue?.employee ?? null;
  return (
    <>
      {!isEqual(oldRate, newRate) && (
        <Typography display="flex" variant="caption" alignItems="center">
          <Typography component="span" variant="inherit" fontWeight="bold">
            Rate:&nbsp;
          </Typography>
          <AuditPropertyValue value={oldRate} />
          <ArrowForwardRounded fontSize="inherit" sx={{ marginX: 1 }} />
          <AuditPropertyValue value={newRate} />
        </Typography>
      )}
      {!isEqual(oldHours, newHours) && (
        <Typography display="flex" variant="caption" alignItems="center">
          <Typography component="span" variant="inherit" fontWeight="bold">
            Hours:&nbsp;
          </Typography>
          <AuditPropertyValue value={oldHours} />
          <ArrowForwardRounded fontSize="inherit" sx={{ marginX: 1 }} />
          <AuditPropertyValue value={newHours} />
        </Typography>
      )}
      {!isEqual(oldEmployee, newEmployee) && (
        <Typography display="flex" variant="caption" alignItems="center">
          <Typography component="span" variant="inherit" fontWeight="bold">
            Employee:&nbsp;
          </Typography>
          <AuditPropertyValue value={oldEmployee?.name ?? null} />
          <ArrowForwardRounded fontSize="inherit" sx={{ marginX: 1 }} />
          <AuditPropertyValue value={newEmployee?.name ?? null} />
        </Typography>
      )}
    </>
  );
}

function renderForDelete(props: Props) {
  const rate = props.property.oldValue?.rate ?? null;
  const hours = props.property.oldValue?.hours ?? null;
  const employee = props.property.oldValue?.employee ?? null;
  return (
    <>
      <Typography display="flex" variant="caption" alignItems="center">
        <Typography component="span" variant="inherit" fontWeight="bold">
          Rate:&nbsp;
        </Typography>
        <AuditPropertyValue value={rate} />
      </Typography>
      <Typography display="flex" variant="caption" alignItems="center">
        <Typography component="span" variant="inherit" fontWeight="bold">
          Hours:&nbsp;
        </Typography>
        <AuditPropertyValue value={hours} />
      </Typography>
      <Typography display="flex" variant="caption" alignItems="center">
        <Typography component="span" variant="inherit" fontWeight="bold">
          Employee:&nbsp;
        </Typography>
        <AuditPropertyValue value={employee?.name ?? null} />
      </Typography>
    </>
  );
}

function renderForCreate(props: Props) {
  const rate = props.property.newValue?.rate ?? null;
  const hours = props.property.newValue?.hours ?? null;
  const employee = props.property.newValue?.employee ?? null;
  return (
    <>
      <Typography display="flex" variant="caption" alignItems="center">
        <Typography component="span" variant="inherit" fontWeight="bold">
          Rate:&nbsp;
        </Typography>
        <AuditPropertyValue value={rate} />
      </Typography>
      <Typography display="flex" variant="caption" alignItems="center">
        <Typography component="span" variant="inherit" fontWeight="bold">
          Hours:&nbsp;
        </Typography>
        <AuditPropertyValue value={hours} />
      </Typography>
      <Typography display="flex" variant="caption" alignItems="center">
        <Typography component="span" variant="inherit" fontWeight="bold">
          Employee:&nbsp;
        </Typography>
        <AuditPropertyValue value={employee?.name ?? null} />
      </Typography>
    </>
  );
}

/**
 * Displays note changes in the audit log
 * Shows which notes were added or removed from a project
 */
export function AuditProjectCostTime(props: Props) {
  if (isEqual(props.property.oldValue, props.property.newValue)) {
    return null;
  }
  if (props.action === 'UPDATE') {
    return renderForUpdate(props);
  }
  if (props.action === 'CREATE') {
    return renderForCreate(props);
  }
  if (props.action === 'DELETE') {
    return renderForDelete(props);
  }
  return null;
}
